import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getDiariesSortedByDateDesc, deleteDiary } from "../api/diaryApi";
import type { Diary } from "../types/diary";
import { useLanguage } from "../i18n/LanguageContext";

export function DiaryListPage() {
  const navigate = useNavigate();
  const [diaries, setDiaries] = useState<Diary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await getDiariesSortedByDateDesc();
        setDiaries(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load diaries");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleDelete(id: number) {
    if (!window.confirm(t.diaryList.deleteConfirm)) return;
    try {
      await deleteDiary(id);
      setDiaries((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to delete");
    }
  }

  if (loading) return <div>{t.diaryList.loading}</div>;
  if (error) return <div>{error}</div>;

  function convertDateTimeToLocal(dateTimeString: string): string {
    const date = new Date(dateTimeString);
    return date.toLocaleString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function calculateAvgPeakFlow(diary: Diary): number | null {
    const readings = [
      diary.peakFlowReadingBeforeSpray,
      diary.peakFlowReadingAfterSpray,
    ].filter(
      (reading) => reading !== undefined && reading !== null,
    ) as number[];
    if (readings.length === 0) return null;
    const sum = readings.reduce((acc, val) => acc + val, 0);
    return sum / readings.length;
  }

  return (
    <main className="diary-page">
      <div className="diary-container">
        <header className="diary-header">
          <h1>{t.diaryList.title}</h1>

          <div className="diary-actions">
            <button
              className="primary-button"
              type="button"
              onClick={() => navigate("/diaries/new")}
            >
              {t.diaryList.newEntry}
            </button>
          </div>
        </header>

        {diaries.length === 0 ? (
          <p className="diary-empty">{t.diaryList.empty}</p>
        ) : (
          <div>
            {diaries.map((d) => (
              <article className="diary-card" key={d.id}>
                <div className="diary-card-header">
                  <div>
                    <strong className="diary-date">
                      {convertDateTimeToLocal(d.dateTime)}
                    </strong>

                    <p className="diary-energy">
                      {t.diaryList.energyLevel}: {d.energyLevel}/10
                    </p>
                    <p className="diary-avg-peak-flow">
                      {t.diaryList.avgPeakFlow}:{" "}
                      {calculateAvgPeakFlow(d) !== null
                        ? `${calculateAvgPeakFlow(d)?.toFixed(2)} L/min`
                        : t.diaryList.na}
                    </p>
                  </div>

                  <div className="diary-card-actions">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => navigate(`/diaries/${d.id}`)}
                    >
                      {t.diaryList.edit}
                    </button>

                    <button
                      className="danger-button"
                      type="button"
                      onClick={() => handleDelete(d.id)}
                    >
                      {t.diaryList.delete}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
